import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import ProxyCard from "../../../legacy-components/proxyCard";
import UnifiedAPIService, { UnifiedAPI } from "../../../services/UnifiedAPIService";
import { ResourceAbility } from "libs/security/authorization";
import ProxyTab from "./proxyTab";
import AddIcon from "@material-ui/icons/Add";
import { Button, Dialog, Snackbar } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { ProxyCreateContent } from "../products/product-forms/ProductProxyDialog";
import Grow from "@material-ui/core/Grow";
import MuiAlert from "@material-ui/lab/Alert";
import Alert from "@material-ui/lab/Alert";

interface ProxyListState {
  isTeamApiLoaded: boolean;
  isSharedApiLoaded: boolean;
  teamapis: any[] | null;
  sharedapis: any[] | null;
  breadcrumb: { title: string; link: string };
  message: string | null;
  sharedApiMessage: string | null;
  apiMessage: string;
  apiMessageType: string;
  isShowProxyCreate: boolean;
}

interface ProxyListProps {
  isGrid?: boolean;
  features?: any;
}

/**
 * @component ProxyList
 * @description Component for listing all Team specific APIs
 */
export const ProxyLists: React.FC<ProxyListProps> = React.memo(({ isGrid, features }) => {
  const history = useHistory();
  const [state, setState] = useState<ProxyListState>({
    isTeamApiLoaded: false,
    isSharedApiLoaded: false,
    teamapis: null,
    sharedapis: null,
    breadcrumb: { title: "API", link: "/home/proxy" },
    message: null,
    sharedApiMessage: null,
    apiMessage: "",
    apiMessageType: "",
    isShowProxyCreate: false
  });

  const loadTeamAPIs = useCallback(async () => {
    try {
      const response = await UnifiedAPIService.request({
        url: UnifiedAPI.parseRequestURL(UnifiedAPI.list_team_apis, {}),
        method: "GET",
      });
      setState(prev => ({
        ...prev,
        teamapis: response.data,
        isTeamApiLoaded: true
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        message: (error as any)?.response?.status === 403 
          ? "You do not have permission to access this feature currently. Please contact your Team owner."
          : null,
        isTeamApiLoaded: true
      }));
    }
  }, []);

  const loadSharedAPIs = useCallback(async () => {
    try {
      const response = await UnifiedAPIService.request({
        url: UnifiedAPI.parseRequestURL(UnifiedAPI.list_shared_apis, {}),
        method: "GET",
      });
      setState(prev => ({
        ...prev,
        sharedapis: response.data,
        isSharedApiLoaded: true
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        sharedApiMessage: (error as any)?.response?.status === 403
          ? "You do not have permission to access this feature currently. Please contact your Team owner."
          : null,
        isSharedApiLoaded: true
      }));
    }
  }, []);

  useEffect(() => {
    loadTeamAPIs();
    loadSharedAPIs();
  }, [loadTeamAPIs, loadSharedAPIs]);

  const showProxyCreationPage = useCallback(() => {
    setState(prev => ({ ...prev, isShowProxyCreate: true }));
  }, []);

  const showSuccessMessage = useCallback((response: any) => {
    const api = response?.apiName || response?.proxyName;
    if (api) {
      history.push("/home/proxy/" + api);
    }
  }, [history]);

  const onSnackBarClose = useCallback(() => {
    setState(prev => ({ ...prev, apiMessage: "", apiMessageType: "" }));
  }, []);

  const handleDialogClose = useCallback(() => {
    setState(prev => ({ ...prev, isShowProxyCreate: false }));
  }, []);

  const renderTeamProxies = useCallback(() => {
    if (!state.isTeamApiLoaded) return null;
    if (state.message) return <div className="message info">{state.message}</div>;
    
    if (!state.teamapis?.length) {
      return (
        <div className="message info">
          <FormattedMessage defaultMessage="No APIs created yet. Click Add API to get started." />
        </div>
      );
    }

    return state.teamapis.map((proxy: any, index: number) => (
      <ProxyCard
        proxy={proxy}
        key={`${proxy.apiId}-${index}`}
        isTeamProxy={true}
        onUpdate={loadTeamAPIs}
      />
    ));
  }, [state.isTeamApiLoaded, state.message, state.teamapis, loadTeamAPIs]);

  const renderSharedProxies = useCallback(() => {
    if (!state.isSharedApiLoaded) return null;
    if (state.sharedApiMessage) return <div className="message info">{state.sharedApiMessage}</div>;

    if (!state.sharedapis?.length) {
      return (
        <div className="message info">
          No shared APIs available at the moment.
        </div>
      );
    }

    return state.sharedapis.map((proxy: any, index: number) => (
      <ProxyCard
        proxy={proxy}
        key={`${proxy.apiId}-${index}`}
        onUpdate={loadSharedAPIs}
      />
    ));
  }, [state.isSharedApiLoaded, state.sharedApiMessage, state.sharedapis, loadSharedAPIs]);

  const header = (
    <h1>
      <ResourceAbility can="create" resource={({ Proxy }) => Proxy.create}>
        <Button
          startIcon={<AddIcon />}
          onClick={showProxyCreationPage}
          variant="contained"
          color="primary"
          disabled={true}
        >
          <FormattedMessage defaultMessage="Add Proxy" />
        </Button>
      </ResourceAbility>
    </h1>
  );

  return (
    <>
    <Alert severity="info">API proxies are now managed through the APIs section. Please use the APIs page for all proxy management.</Alert>
      <Dialog
        fullWidth={true}
        open={state.isShowProxyCreate}
        onClose={handleDialogClose}
      >
        <ProxyCreateContent
          onClose={handleDialogClose}
          onSuccess={showSuccessMessage}
        />
      </Dialog>
      <ResourceAbility can="read" resource={({ Proxy }) => Proxy.list}>
        <ProxyTab
          teamProxies={renderTeamProxies()}
          sharedProxies={renderSharedProxies()}
          header={header}
        />
      </ResourceAbility>
      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        open={!!state.apiMessage}
        onClose={onSnackBarClose}
        TransitionComponent={Grow}
        message={state.apiMessage}
        autoHideDuration={6000}
        className="snackbar"
      >
        <MuiAlert
          elevation={6}
          variant="filled"
        //  severity={state.apiMessageType}
        >
          {state.apiMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
});

const mapStateToProps = (state: any) => ({
  isGrid: state.common.isGrid,
  features: state.common.features,
});

export default connect(mapStateToProps)(ProxyLists);
